import Add from '@mui/icons-material/Add';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { ModelError_Item } from '@treadinc/horizon-api-spec';
import axios from 'axios';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import DataGridPager from '~components/DataGrid/DataGridPager';
import actionsColumn from '~components/DataTable/actionsColumn';
import DataTable from '~components/DataTable/DataTable';
import { DataTableColumn } from '~components/DataTable/dataTableColumn';
import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { DEFAULT_API_PAGE_SIZE } from '~constants/consts';
import { PrevailingWageBenefit } from '~hooks/usePrevailingWageBenefits/models';
import usePrevailingWageBenefits from '~hooks/usePrevailingWageBenefits/usePrevailingWageBenefits';
import SettingsPageHeader from '~pages/Settings/components/SettingsPageHeader';
import connection from '~services/connectionModule';
import { Pagination, PaginationLink } from '~services/pagination';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { getPageNumber } from '~utils/pagination';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import BenefitForm, { BenefitFormHandler } from './BenefitForm';
import { BenefitDto, BenefitDtoAsyncError } from './benefitSchema';

type BenefitDialogState = {
  benefit?: PrevailingWageBenefit;
  isOpen: boolean;
  isSubmitting: boolean;
};

export const PrevailingWageBenefitsManagement = observer(() => {
  const { userStore, toasterStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const { fetchBenefits, createBenefit, updateBenefit, deleteBenefit } =
    usePrevailingWageBenefits();

  const [isLoading, setIsLoading] = useState(false);
  const [benefits, setBenefits] = useState<PrevailingWageBenefit[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    limit: DEFAULT_API_PAGE_SIZE,
    page: 1,
  });

  const formRef = useRef<BenefitFormHandler>(null);

  const [formDialogState, setFormDialogState] = useState<BenefitDialogState>({
    isOpen: false,
    isSubmitting: false,
  });
  const [formDialogAsyncErrors, setFormDialogAsyncErrors] = useState<
    BenefitDtoAsyncError[]
  >([]);

  const [deleteDialogState, setDeleteDialogState] = useState<BenefitDialogState>({
    isOpen: false,
    isSubmitting: false,
  });

  const fetchData = useCallback(
    async (link?: PaginationLink) => {
      const cursor = link ? pagination[link] : undefined;
      setIsLoading(true);
      try {
        const result = await fetchBenefits({ companyId, link, cursor });

        if (result) {
          setBenefits(result.data);
          setPagination({
            ...result.pagination,
            limit: pagination.limit,
            page: link ? getPageNumber(pagination.page, link) : 1,
          });
        }
      } finally {
        setIsLoading(false);
      }
    },
    [companyId, pagination, fetchBenefits],
  );

  useEffect(() => {
    fetchData();
  }, [companyId]);

  const handleCreateClick = useCallback(() => {
    setFormDialogAsyncErrors([]);
    setFormDialogState({ isOpen: true, isSubmitting: false });
  }, []);

  const handleEditClick = useCallback((benefit: PrevailingWageBenefit) => {
    setFormDialogAsyncErrors([]);
    setFormDialogState({ isOpen: true, isSubmitting: false, benefit });
  }, []);

  const handleDeleteClick = useCallback((benefit: PrevailingWageBenefit) => {
    setDeleteDialogState({ isOpen: true, isSubmitting: false, benefit });
  }, []);

  const handleCloseFormDialog = useCallback(() => {
    setFormDialogAsyncErrors([]);
    setFormDialogState((state) => ({ ...state, isOpen: false }));
  }, []);

  const handleCloseDeleteDialog = useCallback(() => {
    setDeleteDialogState((state) => ({ ...state, isOpen: false }));
  }, []);

  const handleSubmitFormDialog = useCallback(async () => {
    const formData = await formRef.current?.onSubmit();

    if (!formData) return;

    const benefitId = formDialogState.benefit?.id;

    try {
      setFormDialogAsyncErrors([]);
      setFormDialogState((state) => ({ ...state, isSubmitting: true }));

      if (benefitId) {
        await updateBenefit({
          companyId,
          benefitId,
          benefit: { name: formData.name },
        });
        toasterStore.push(
          alert(t('administration.prevailing_wage_benefits.updated'), AlertTypes.success),
        );
      } else {
        await createBenefit({
          companyId,
          benefit: { name: formData.name },
        });
        toasterStore.push(
          alert(t('administration.prevailing_wage_benefits.created'), AlertTypes.success),
        );
      }

      setFormDialogState({ isOpen: false, isSubmitting: false });
      fetchData();
    } catch (error) {
      setFormDialogState((state) => ({ ...state, isSubmitting: false }));

      if (axios.isAxiosError(error) && error.response?.status === 422) {
        const errorsArray = (error.response?.data?.error?.errors ??
          []) as ModelError_Item[];

        const asyncErrors: BenefitDtoAsyncError[] = [];
        const fieldMap: Record<string, keyof BenefitDto> = { name: 'name' };

        errorsArray.forEach((errorsArrayItem) => {
          const { field, message } = errorsArrayItem;
          const formField = fieldMap[field];

          if (formField && message) {
            asyncErrors.push({ field: formField, message });
          }
        });

        if (asyncErrors.length) {
          setFormDialogAsyncErrors(asyncErrors);
          return;
        }

        connection.handleRequestError(
          error,
          benefitId
            ? t('error_messages.prevailing_wage_benefits.failed_to_update')
            : t('error_messages.prevailing_wage_benefits.failed_to_create'),
        );
      }
    }
  }, [
    formDialogState.benefit?.id,
    companyId,
    updateBenefit,
    createBenefit,
    toasterStore,
    fetchData,
  ]);

  const handleDeleteBenefit = useCallback(async () => {
    const benefitId = deleteDialogState.benefit?.id;
    if (!benefitId) return;

    try {
      setDeleteDialogState((state) => ({ ...state, isSubmitting: true }));
      await deleteBenefit({ companyId, benefitId });
      toasterStore.push(
        alert(t('administration.prevailing_wage_benefits.deleted'), AlertTypes.success),
      );
      setDeleteDialogState({ isOpen: false, isSubmitting: false });
      fetchData();
    } catch {
      setDeleteDialogState((state) => ({ ...state, isSubmitting: false }));
    }
  }, [deleteDialogState.benefit?.id, companyId, deleteBenefit, toasterStore, fetchData]);

  const columns = useMemo<Array<DataTableColumn<PrevailingWageBenefit>>>(
    () => [
      {
        field: 'name',
        headerName: t('form_fields.name'),
        valueGetter: (benefit) => benefit.name,
      },
      actionsColumn((benefit) => (
        <Box sx={{ display: 'flex' }}>
          <IconButton size="small" onClick={() => handleEditClick(benefit)}>
            <Edit />
          </IconButton>

          <IconButton size="small" onClick={() => handleDeleteClick(benefit)}>
            <Delete />
          </IconButton>
        </Box>
      )),
    ],
    [handleEditClick, handleDeleteClick],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <SettingsPageHeader
        title={t('administration.prevailing_wage_benefits.title')}
        description={t('settings.page_descriptions.prevailing_wage_benefits')}
      />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <SmallButton
          color="brandV2Yellow"
          onClick={handleCreateClick}
          startIcon={<Add />}
        >
          {t('administration.prevailing_wage_benefits.create')}
        </SmallButton>
      </Box>

      <DataTable
        id="pw-benefits-grid"
        loading={isLoading}
        rows={benefits}
        columns={columns}
        headerTrailingComponent={
          <DataGridPager
            isFetching={isLoading}
            onNavigate={fetchData}
            pagination={pagination}
          />
        }
      />

      <Dialog
        isOpen={formDialogState.isOpen}
        preventCloseOnBackdropClick
        preventCloseOnEscapeKeyDown
      >
        <DialogHeader onClose={handleCloseFormDialog}>
          {formDialogState.benefit
            ? t('administration.prevailing_wage_benefits.edit')
            : t('administration.prevailing_wage_benefits.create')}
        </DialogHeader>

        <DialogContent>
          <BenefitForm
            ref={formRef}
            asyncErrors={formDialogAsyncErrors}
            benefit={formDialogState.benefit}
          />
        </DialogContent>

        <DialogActions>
          <SmallButton
            color="secondary"
            disabled={formDialogState.isSubmitting}
            onClick={handleCloseFormDialog}
            variant="outlined"
          >
            {t('actions.cancel')}
          </SmallButton>

          <SmallLoadingButton
            color="brandV2Yellow"
            loading={formDialogState.isSubmitting}
            onClick={handleSubmitFormDialog}
          >
            {formDialogState.benefit ? t('actions.update') : t('actions.create')}
          </SmallLoadingButton>
        </DialogActions>
      </Dialog>

      <ConfirmationDialog
        isBusy={deleteDialogState.isSubmitting}
        isOpen={deleteDialogState.isOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleDeleteBenefit}
        title={t('administration.prevailing_wage_benefits.delete')}
      >
        <Typography>
          {t('administration.prevailing_wage_benefits.delete_confirmation')}
        </Typography>
      </ConfirmationDialog>
    </Box>
  );
});
